/**
 * Mock Process Builder Data
 *
 * Realistic sample processes demonstrating various use cases:
 * - Opportunity Won handling
 * - High-value lead assignment
 * - Case escalation
 * - Contract renewal reminders
 * - Welcome email sequences
 *
 * @module data/mockProcesses
 */

import {
  Process,
  ProcessExecution,
  ProcessCriteriaGroup,
  ProcessAction,
  ScheduledAction,
  FieldUpdateConfig,
  CreateRecordConfig,
  SendEmailConfig,
  CreateTaskConfig,
  PostToFeedConfig
} from '../types/processBuilder';

// Helper to generate IDs
const id = (prefix: string, index: number): string => `${prefix}_mock_${index}`;

// Timestamps for mock data
const now = new Date();
const daysAgo = (days: number): string =>
  new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();

// ============================================
// MOCK PROCESSES
// ============================================

/**
 * Process 1: Opportunity Won - Account Update
 * When an Opportunity is closed won:
 * - Update Account total revenue
 * - Create success celebration task
 * - Send thank you email to contact
 * - Post to account feed
 */
const opportunityWonProcess: Process = {
  id: id('proc', 1),
  name: 'Opportunity Won - Account Update',
  description:
    'Automatically updates account information, creates follow-up tasks, and sends notifications when an opportunity is closed won.',
  apiName: 'opportunity_won_account_update',
  objectType: 'Opportunity',
  triggerType: 'record_updated',
  isActive: true,
  status: 'active',
  category: 'Sales',

  entryCriteria: {
    id: id('ec', 1),
    name: 'Entry Criteria',
    conditionLogic: 'AND',
    conditions: [
      {
        id: id('cond', 1),
        field: 'Stage',
        operator: 'is_changed_to',
        value: 'Closed Won'
      }
    ],
    order: 0
  },

  reevaluateOnUpdate: false,

  criteriaGroups: [
    {
      id: id('cg', 1),
      name: 'High Value Deal (>$100K)',
      description: 'Special handling for deals over $100,000',
      conditionLogic: 'AND',
      conditions: [
        {
          id: id('cond', 2),
          field: 'Amount',
          operator: 'greater_than',
          value: 100000,
          valueType: 'number'
        }
      ],
      order: 1
    },
    {
      id: id('cg', 2),
      name: 'Standard Deal',
      description: 'Standard processing for all other deals',
      conditionLogic: 'AND',
      conditions: [],
      order: 2
    }
  ],

  immediateActions: {
    [id('cg', 1)]: [
      {
        id: id('act', 1),
        type: 'field_update',
        name: 'Update Account - High Value',
        description: 'Mark account as key account and update revenue',
        order: 1,
        config: {
          targetObject: 'related',
          relatedField: 'accountId',
          relatedObjectType: 'Account',
          fieldUpdates: [
            {
              field: 'IsKeyAccount',
              fieldLabel: 'Key Account',
              updateType: 'literal',
              value: 'true'
            },
            {
              field: 'TotalRevenue',
              fieldLabel: 'Total Revenue',
              updateType: 'formula',
              value: 'TotalRevenue + {!Opportunity.Amount}'
            }
          ]
        } as FieldUpdateConfig
      },
      {
        id: id('act', 2),
        type: 'create_task',
        name: 'Create Executive Follow-up Task',
        description: 'Create task for executive team to follow up on high-value win',
        order: 2,
        config: {
          subject: 'Executive Follow-up: {!Opportunity.Name} - ${!Opportunity.Amount}',
          assignToType: 'user',
          assignTo: 'executive_user',
          dueDateType: 'days_from_now',
          dueDays: 3,
          priority: 'High',
          description:
            'High-value opportunity won. Schedule executive check-in with customer to strengthen relationship.'
        } as CreateTaskConfig
      },
      {
        id: id('act', 3),
        type: 'send_email',
        name: 'Send VIP Thank You Email',
        description: 'Send personalized thank you from executive',
        order: 3,
        config: {
          templateId: 'vip_thank_you_template',
          templateName: 'VIP Thank You',
          recipientType: 'related_user',
          recipient: 'Contact.Email'
        } as SendEmailConfig
      },
      {
        id: id('act', 4),
        type: 'post_to_feed',
        name: 'Celebrate Win',
        description: 'Post celebration to account feed',
        order: 4,
        config: {
          feedTarget: 'record',
          targetId: '{!Opportunity.AccountId}',
          postContent:
            '🎉 Major win! {!Opportunity.Name} closed for ${!Opportunity.Amount}. Great job team!',
          mentionOwner: true
        } as PostToFeedConfig
      }
    ],
    [id('cg', 2)]: [
      {
        id: id('act', 5),
        type: 'field_update',
        name: 'Update Account Revenue',
        description: 'Add opportunity amount to account total revenue',
        order: 1,
        config: {
          targetObject: 'related',
          relatedField: 'accountId',
          relatedObjectType: 'Account',
          fieldUpdates: [
            {
              field: 'TotalRevenue',
              fieldLabel: 'Total Revenue',
              updateType: 'formula',
              value: 'TotalRevenue + {!Opportunity.Amount}'
            }
          ]
        } as FieldUpdateConfig
      },
      {
        id: id('act', 6),
        type: 'create_task',
        name: 'Create Thank You Task',
        description: 'Create task to send thank you',
        order: 2,
        config: {
          subject: 'Send Thank You: {!Opportunity.Name}',
          assignToType: 'owner',
          dueDateType: 'tomorrow',
          priority: 'Normal',
          description: 'Send thank you note to customer for their business.'
        } as CreateTaskConfig
      },
      {
        id: id('act', 7),
        type: 'send_email',
        name: 'Send Thank You Email',
        description: 'Send standard thank you email',
        order: 3,
        config: {
          templateId: 'standard_thank_you_template',
          templateName: 'Standard Thank You',
          recipientType: 'related_user',
          recipient: 'Contact.Email'
        } as SendEmailConfig
      }
    ]
  },

  scheduledActions: {},

  currentVersion: 1,
  versions: [
    {
      id: id('pv', 1),
      versionNumber: 1,
      status: 'active',
      createdDate: daysAgo(30),
      createdBy: 'admin_user',
      activatedDate: daysAgo(28),
      activatedBy: 'admin_user',
      description: 'Initial version'
    }
  ],

  createdDate: daysAgo(30),
  createdBy: 'admin_user',
  createdByName: 'System Administrator',
  lastModifiedDate: daysAgo(5),
  lastModifiedBy: 'admin_user',
  lastModifiedByName: 'System Administrator',
  lastRunDate: daysAgo(1),
  executionCount: 47,
  successCount: 45,
  failureCount: 2
};

/**
 * Process 2: High-Value Lead Assignment
 * When a Lead is created with high score:
 * - Auto-assign to sales team
 * - Create priority follow-up task
 * - Send internal notification
 */
const highValueLeadProcess: Process = {
  id: id('proc', 2),
  name: 'High-Value Lead Assignment',
  description:
    'Automatically routes high-scoring leads from website to the appropriate sales team with priority follow-up tasks.',
  apiName: 'high_value_lead_assignment',
  objectType: 'Lead',
  triggerType: 'record_created',
  isActive: true,
  status: 'active',
  category: 'Marketing',

  entryCriteria: {
    id: id('ec', 2),
    name: 'Entry Criteria',
    conditionLogic: 'AND',
    conditions: [
      {
        id: id('cond', 10),
        field: 'LeadScore',
        operator: 'greater_than',
        value: 80,
        valueType: 'number'
      },
      {
        id: id('cond', 11),
        field: 'Source',
        operator: 'equals',
        value: 'Website'
      }
    ],
    order: 0
  },

  reevaluateOnUpdate: false,

  criteriaGroups: [
    {
      id: id('cg', 10),
      name: 'Enterprise Lead',
      description: 'Enterprise-level leads with company size > 1000',
      conditionLogic: 'AND',
      conditions: [
        {
          id: id('cond', 12),
          field: 'CompanySize',
          operator: 'greater_than',
          value: 1000,
          valueType: 'number'
        }
      ],
      order: 1,
      stopProcessing: true
    },
    {
      id: id('cg', 11),
      name: 'Mid-Market Lead',
      description: 'Mid-market leads with company size 100-1000',
      conditionLogic: 'AND',
      conditions: [
        {
          id: id('cond', 13),
          field: 'CompanySize',
          operator: 'greater_or_equal',
          value: 100,
          valueType: 'number'
        },
        {
          id: id('cond', 14),
          field: 'CompanySize',
          operator: 'less_or_equal',
          value: 1000,
          valueType: 'number'
        }
      ],
      order: 2,
      stopProcessing: true
    },
    {
      id: id('cg', 12),
      name: 'SMB Lead',
      description: 'Small business leads',
      conditionLogic: 'AND',
      conditions: [],
      order: 3
    }
  ],

  immediateActions: {
    [id('cg', 10)]: [
      {
        id: id('act', 10),
        type: 'field_update',
        name: 'Assign to Enterprise Team',
        order: 1,
        config: {
          targetObject: 'current',
          fieldUpdates: [
            {
              field: 'OwnerId',
              fieldLabel: 'Owner',
              updateType: 'literal',
              value: 'enterprise_queue'
            },
            {
              field: 'Rating',
              fieldLabel: 'Rating',
              updateType: 'literal',
              value: 'Hot'
            }
          ]
        } as FieldUpdateConfig
      },
      {
        id: id('act', 11),
        type: 'create_task',
        name: 'Priority Enterprise Follow-up',
        order: 2,
        config: {
          subject: 'URGENT: Enterprise Lead - {!Lead.Company}',
          assignToType: 'queue',
          assignTo: 'enterprise_queue',
          dueDateType: 'today',
          priority: 'High',
          description:
            'High-value enterprise lead requires immediate follow-up. Lead Score: {!Lead.LeadScore}'
        } as CreateTaskConfig
      }
    ],
    [id('cg', 11)]: [
      {
        id: id('act', 12),
        type: 'field_update',
        name: 'Assign to Mid-Market Team',
        order: 1,
        config: {
          targetObject: 'current',
          fieldUpdates: [
            {
              field: 'OwnerId',
              fieldLabel: 'Owner',
              updateType: 'literal',
              value: 'midmarket_queue'
            },
            {
              field: 'Rating',
              fieldLabel: 'Rating',
              updateType: 'literal',
              value: 'Warm'
            }
          ]
        } as FieldUpdateConfig
      },
      {
        id: id('act', 13),
        type: 'create_task',
        name: 'Mid-Market Follow-up',
        order: 2,
        config: {
          subject: 'High-Score Lead: {!Lead.Company}',
          assignToType: 'queue',
          assignTo: 'midmarket_queue',
          dueDateType: 'tomorrow',
          priority: 'Normal',
          description: 'Qualified mid-market lead. Lead Score: {!Lead.LeadScore}'
        } as CreateTaskConfig
      }
    ],
    [id('cg', 12)]: [
      {
        id: id('act', 14),
        type: 'field_update',
        name: 'Assign to SMB Team',
        order: 1,
        config: {
          targetObject: 'current',
          fieldUpdates: [
            {
              field: 'OwnerId',
              fieldLabel: 'Owner',
              updateType: 'literal',
              value: 'smb_queue'
            }
          ]
        } as FieldUpdateConfig
      }
    ]
  },

  scheduledActions: {},

  currentVersion: 2,
  versions: [
    {
      id: id('pv', 10),
      versionNumber: 1,
      status: 'inactive',
      createdDate: daysAgo(60),
      createdBy: 'admin_user',
      activatedDate: daysAgo(58),
      activatedBy: 'admin_user',
      deactivatedDate: daysAgo(20),
      deactivatedBy: 'admin_user',
      description: 'Initial version'
    },
    {
      id: id('pv', 11),
      versionNumber: 2,
      status: 'active',
      createdDate: daysAgo(20),
      createdBy: 'admin_user',
      activatedDate: daysAgo(18),
      activatedBy: 'admin_user',
      description: 'Added SMB handling and updated thresholds'
    }
  ],

  createdDate: daysAgo(60),
  createdBy: 'admin_user',
  createdByName: 'System Administrator',
  lastModifiedDate: daysAgo(18),
  lastModifiedBy: 'admin_user',
  lastModifiedByName: 'System Administrator',
  lastRunDate: daysAgo(0),
  executionCount: 156,
  successCount: 152,
  failureCount: 4
};

/**
 * Process 3: Case Escalation Process
 * When a Case is updated to High priority:
 * - Set escalation flag
 * - Notify manager
 * - Create urgent task
 * - Update SLA fields
 */
const caseEscalationProcess: Process = {
  id: id('proc', 3),
  name: 'Case Escalation Process',
  description:
    'Handles automatic escalation of high-priority cases, including manager notification and SLA tracking.',
  apiName: 'case_escalation_process',
  objectType: 'Case',
  triggerType: 'record_updated',
  isActive: true,
  status: 'active',
  category: 'Service',

  entryCriteria: {
    id: id('ec', 3),
    name: 'Entry Criteria',
    conditionLogic: 'AND',
    conditions: [
      {
        id: id('cond', 20),
        field: 'Priority',
        operator: 'equals',
        value: 'High'
      },
      {
        id: id('cond', 21),
        field: 'Status',
        operator: 'not_equals',
        value: 'Closed'
      },
      {
        id: id('cond', 22),
        field: 'IsEscalated',
        operator: 'equals',
        value: false,
        valueType: 'boolean'
      }
    ],
    order: 0
  },

  reevaluateOnUpdate: true,

  criteriaGroups: [
    {
      id: id('cg', 20),
      name: 'VIP Customer',
      description: 'VIP customer escalation handling',
      conditionLogic: 'AND',
      conditions: [
        {
          id: id('cond', 23),
          field: 'Account.IsVIP',
          operator: 'equals',
          value: true,
          valueType: 'boolean'
        }
      ],
      order: 1
    },
    {
      id: id('cg', 21),
      name: 'Standard Escalation',
      description: 'Standard escalation handling',
      conditionLogic: 'AND',
      conditions: [],
      order: 2
    }
  ],

  immediateActions: {
    [id('cg', 20)]: [
      {
        id: id('act', 20),
        type: 'field_update',
        name: 'Set VIP Escalation Fields',
        order: 1,
        config: {
          targetObject: 'current',
          fieldUpdates: [
            {
              field: 'IsEscalated',
              fieldLabel: 'Escalated',
              updateType: 'literal',
              value: 'true'
            },
            {
              field: 'EscalationLevel',
              fieldLabel: 'Escalation Level',
              updateType: 'literal',
              value: 'Executive'
            },
            {
              field: 'SLA_Target__c',
              fieldLabel: 'SLA Target',
              updateType: 'literal',
              value: '2' // 2 hours for VIP
            }
          ]
        } as FieldUpdateConfig
      },
      {
        id: id('act', 21),
        type: 'send_email',
        name: 'Notify Executive Team',
        order: 2,
        config: {
          templateId: 'vip_escalation_template',
          templateName: 'VIP Case Escalation',
          recipientType: 'user',
          recipient: 'executive_support',
          ccRecipients: ['support_manager']
        } as SendEmailConfig
      },
      {
        id: id('act', 22),
        type: 'create_task',
        name: 'Create Urgent Resolution Task',
        order: 3,
        config: {
          subject: 'URGENT VIP: {!Case.Subject}',
          assignToType: 'user',
          assignTo: 'executive_support',
          dueDateType: 'today',
          priority: 'High',
          description:
            'VIP customer case escalated. Immediate resolution required. Case #: {!Case.CaseNumber}'
        } as CreateTaskConfig
      }
    ],
    [id('cg', 21)]: [
      {
        id: id('act', 23),
        type: 'field_update',
        name: 'Set Escalation Fields',
        order: 1,
        config: {
          targetObject: 'current',
          fieldUpdates: [
            {
              field: 'IsEscalated',
              fieldLabel: 'Escalated',
              updateType: 'literal',
              value: 'true'
            },
            {
              field: 'EscalationLevel',
              fieldLabel: 'Escalation Level',
              updateType: 'literal',
              value: 'Manager'
            },
            {
              field: 'SLA_Target__c',
              fieldLabel: 'SLA Target',
              updateType: 'literal',
              value: '4' // 4 hours standard
            }
          ]
        } as FieldUpdateConfig
      },
      {
        id: id('act', 24),
        type: 'send_email',
        name: 'Notify Manager',
        order: 2,
        config: {
          templateId: 'case_escalation_template',
          templateName: 'Case Escalation Notification',
          recipientType: 'related_user',
          recipient: 'Owner.ManagerId'
        } as SendEmailConfig
      },
      {
        id: id('act', 25),
        type: 'create_task',
        name: 'Create Resolution Task',
        order: 3,
        config: {
          subject: 'Escalated: {!Case.Subject}',
          assignToType: 'owner',
          dueDateType: 'today',
          priority: 'High',
          description: 'Case has been escalated. Priority resolution required.'
        } as CreateTaskConfig
      }
    ]
  },

  scheduledActions: {},

  currentVersion: 1,
  versions: [
    {
      id: id('pv', 20),
      versionNumber: 1,
      status: 'active',
      createdDate: daysAgo(45),
      createdBy: 'admin_user',
      activatedDate: daysAgo(44),
      activatedBy: 'admin_user',
      description: 'Initial version'
    }
  ],

  createdDate: daysAgo(45),
  createdBy: 'admin_user',
  createdByName: 'System Administrator',
  lastModifiedDate: daysAgo(10),
  lastModifiedBy: 'support_manager',
  lastModifiedByName: 'Support Manager',
  lastRunDate: daysAgo(0),
  executionCount: 89,
  successCount: 87,
  failureCount: 2
};

/**
 * Process 4: Contract Renewal Reminder
 * Scheduled process for contract renewal management:
 * - 30 days before: Send reminder email
 * - 14 days before: Create renewal opportunity
 * - 7 days before: Escalate to manager
 */
const contractRenewalProcess: Process = {
  id: id('proc', 4),
  name: 'Contract Renewal Reminder',
  description:
    'Automated contract renewal workflow that sends reminders, creates renewal opportunities, and escalates as needed.',
  apiName: 'contract_renewal_reminder',
  objectType: 'Contract',
  triggerType: 'record_updated',
  isActive: true,
  status: 'active',
  category: 'Sales',

  entryCriteria: {
    id: id('ec', 4),
    name: 'Entry Criteria',
    conditionLogic: 'AND',
    conditions: [
      {
        id: id('cond', 30),
        field: 'Status',
        operator: 'equals',
        value: 'Active'
      },
      {
        id: id('cond', 31),
        field: 'AutoRenewal',
        operator: 'equals',
        value: false,
        valueType: 'boolean'
      }
    ],
    order: 0
  },

  reevaluateOnUpdate: false,

  criteriaGroups: [
    {
      id: id('cg', 30),
      name: 'All Active Contracts',
      description: 'Process all active non-auto-renewal contracts',
      conditionLogic: 'AND',
      conditions: [],
      order: 1
    }
  ],

  immediateActions: {},

  scheduledActions: {
    [id('cg', 30)]: [
      {
        id: id('sa', 1),
        name: '30 Day Reminder',
        description: 'Send initial renewal reminder 30 days before expiration',
        scheduleType: 'days_before',
        scheduleValue: 30,
        baseDateField: 'EndDate',
        baseDateFieldLabel: 'Contract End Date',
        isActive: true,
        actions: [
          {
            id: id('act', 30),
            type: 'send_email',
            name: 'Send 30 Day Reminder',
            order: 1,
            config: {
              templateId: 'contract_renewal_30day',
              templateName: '30 Day Renewal Reminder',
              recipientType: 'related_user',
              recipient: 'Account.OwnerId'
            } as SendEmailConfig
          },
          {
            id: id('act', 31),
            type: 'create_task',
            name: 'Create Renewal Review Task',
            order: 2,
            config: {
              subject: 'Contract Renewal Review: {!Contract.ContractNumber}',
              assignToType: 'owner',
              dueDateType: 'days_from_now',
              dueDays: 7,
              priority: 'Normal',
              description:
                'Contract expires in 30 days. Review and initiate renewal process if appropriate.'
            } as CreateTaskConfig
          }
        ]
      },
      {
        id: id('sa', 2),
        name: '14 Day - Create Opportunity',
        description: 'Create renewal opportunity 14 days before expiration',
        scheduleType: 'days_before',
        scheduleValue: 14,
        baseDateField: 'EndDate',
        baseDateFieldLabel: 'Contract End Date',
        isActive: true,
        actions: [
          {
            id: id('act', 32),
            type: 'create_record',
            name: 'Create Renewal Opportunity',
            order: 1,
            config: {
              objectType: 'Opportunity',
              objectLabel: 'Opportunity',
              fieldMappings: [
                {
                  field: 'Name',
                  fieldLabel: 'Name',
                  valueType: 'formula',
                  value: 'Renewal - {!Contract.Account.Name}'
                },
                {
                  field: 'AccountId',
                  fieldLabel: 'Account',
                  valueType: 'field_reference',
                  value: 'Contract.AccountId'
                },
                {
                  field: 'Amount',
                  fieldLabel: 'Amount',
                  valueType: 'field_reference',
                  value: 'Contract.ContractValue'
                },
                {
                  field: 'CloseDate',
                  fieldLabel: 'Close Date',
                  valueType: 'field_reference',
                  value: 'Contract.EndDate'
                },
                {
                  field: 'Stage',
                  fieldLabel: 'Stage',
                  valueType: 'literal',
                  value: 'Discovery'
                },
                {
                  field: 'Type',
                  fieldLabel: 'Type',
                  valueType: 'literal',
                  value: 'Renewal'
                }
              ]
            } as CreateRecordConfig
          },
          {
            id: id('act', 33),
            type: 'send_email',
            name: 'Send 14 Day Reminder',
            order: 2,
            config: {
              templateId: 'contract_renewal_14day',
              templateName: '14 Day Renewal Reminder',
              recipientType: 'related_user',
              recipient: 'Account.OwnerId'
            } as SendEmailConfig
          }
        ]
      },
      {
        id: id('sa', 3),
        name: '7 Day - Manager Escalation',
        description: 'Escalate to manager 7 days before expiration if not renewed',
        scheduleType: 'days_before',
        scheduleValue: 7,
        baseDateField: 'EndDate',
        baseDateFieldLabel: 'Contract End Date',
        isActive: true,
        actions: [
          {
            id: id('act', 34),
            type: 'send_email',
            name: 'Escalate to Manager',
            order: 1,
            config: {
              templateId: 'contract_renewal_escalation',
              templateName: 'Contract Renewal Escalation',
              recipientType: 'related_user',
              recipient: 'Account.Owner.ManagerId',
              ccRecipients: ['{!Contract.Account.OwnerId}']
            } as SendEmailConfig
          },
          {
            id: id('act', 35),
            type: 'create_task',
            name: 'Create Urgent Renewal Task',
            order: 2,
            config: {
              subject: 'URGENT: Contract Expiring - {!Contract.ContractNumber}',
              assignToType: 'related_user',
              assignTo: 'Account.Owner.ManagerId',
              dueDateType: 'days_from_now',
              dueDays: 3,
              priority: 'High',
              description:
                'Contract expires in 7 days and has not been renewed. Immediate action required.'
            } as CreateTaskConfig
          }
        ]
      }
    ]
  },

  currentVersion: 1,
  versions: [
    {
      id: id('pv', 30),
      versionNumber: 1,
      status: 'active',
      createdDate: daysAgo(90),
      createdBy: 'admin_user',
      activatedDate: daysAgo(88),
      activatedBy: 'admin_user',
      description: 'Initial version'
    }
  ],

  createdDate: daysAgo(90),
  createdBy: 'admin_user',
  createdByName: 'System Administrator',
  lastModifiedDate: daysAgo(15),
  lastModifiedBy: 'sales_ops',
  lastModifiedByName: 'Sales Operations',
  lastRunDate: daysAgo(1),
  executionCount: 234,
  successCount: 230,
  failureCount: 4
};

/**
 * Process 5: Welcome Email Sequence
 * When a Contact is created with email opt-in:
 * - Send immediate welcome email
 * - Schedule follow-up emails
 * - Track engagement
 */
const welcomeEmailProcess: Process = {
  id: id('proc', 5),
  name: 'Welcome Email Sequence',
  description:
    'Automated onboarding email sequence for new contacts who have opted in to communications.',
  apiName: 'welcome_email_sequence',
  objectType: 'Contact',
  triggerType: 'record_created',
  isActive: true,
  status: 'active',
  category: 'Marketing',

  entryCriteria: {
    id: id('ec', 5),
    name: 'Entry Criteria',
    conditionLogic: 'AND',
    conditions: [
      {
        id: id('cond', 40),
        field: 'EmailOptIn',
        operator: 'equals',
        value: true,
        valueType: 'boolean'
      },
      {
        id: id('cond', 41),
        field: 'Email',
        operator: 'is_not_null',
        value: null
      }
    ],
    order: 0
  },

  reevaluateOnUpdate: false,

  criteriaGroups: [
    {
      id: id('cg', 40),
      name: 'Customer Contact',
      description: 'Contact linked to an existing account',
      conditionLogic: 'AND',
      conditions: [
        {
          id: id('cond', 42),
          field: 'AccountId',
          operator: 'is_not_null',
          value: null
        }
      ],
      order: 1
    },
    {
      id: id('cg', 41),
      name: 'Prospect Contact',
      description: 'Contact without an account',
      conditionLogic: 'AND',
      conditions: [
        {
          id: id('cond', 43),
          field: 'AccountId',
          operator: 'is_null',
          value: null
        }
      ],
      order: 2
    }
  ],

  immediateActions: {
    [id('cg', 40)]: [
      {
        id: id('act', 40),
        type: 'send_email',
        name: 'Send Customer Welcome Email',
        order: 1,
        config: {
          templateId: 'customer_welcome_template',
          templateName: 'Customer Welcome',
          recipientType: 'email_field',
          recipient: 'Email'
        } as SendEmailConfig
      },
      {
        id: id('act', 41),
        type: 'field_update',
        name: 'Set Welcome Email Sent',
        order: 2,
        config: {
          targetObject: 'current',
          fieldUpdates: [
            {
              field: 'WelcomeEmailSent',
              fieldLabel: 'Welcome Email Sent',
              updateType: 'literal',
              value: 'true'
            },
            {
              field: 'WelcomeEmailDate',
              fieldLabel: 'Welcome Email Date',
              updateType: 'formula',
              value: 'NOW()'
            }
          ]
        } as FieldUpdateConfig
      }
    ],
    [id('cg', 41)]: [
      {
        id: id('act', 42),
        type: 'send_email',
        name: 'Send Prospect Welcome Email',
        order: 1,
        config: {
          templateId: 'prospect_welcome_template',
          templateName: 'Prospect Welcome',
          recipientType: 'email_field',
          recipient: 'Email'
        } as SendEmailConfig
      },
      {
        id: id('act', 43),
        type: 'field_update',
        name: 'Set Welcome Email Sent',
        order: 2,
        config: {
          targetObject: 'current',
          fieldUpdates: [
            {
              field: 'WelcomeEmailSent',
              fieldLabel: 'Welcome Email Sent',
              updateType: 'literal',
              value: 'true'
            }
          ]
        } as FieldUpdateConfig
      }
    ]
  },

  scheduledActions: {
    [id('cg', 40)]: [
      {
        id: id('sa', 10),
        name: 'Customer Resources Email (3 days)',
        description: 'Send helpful resources to new customer contacts',
        scheduleType: 'days_after',
        scheduleValue: 3,
        baseDateField: 'CreatedDate',
        baseDateFieldLabel: 'Created Date',
        isActive: true,
        actions: [
          {
            id: id('act', 44),
            type: 'send_email',
            name: 'Send Resources Email',
            order: 1,
            config: {
              templateId: 'customer_resources_template',
              templateName: 'Customer Resources',
              recipientType: 'email_field',
              recipient: 'Email'
            } as SendEmailConfig
          }
        ]
      },
      {
        id: id('sa', 11),
        name: 'Customer Survey (7 days)',
        description: 'Send satisfaction survey',
        scheduleType: 'days_after',
        scheduleValue: 7,
        baseDateField: 'CreatedDate',
        baseDateFieldLabel: 'Created Date',
        isActive: true,
        actions: [
          {
            id: id('act', 45),
            type: 'send_email',
            name: 'Send Survey Email',
            order: 1,
            config: {
              templateId: 'customer_survey_template',
              templateName: 'Customer Survey',
              recipientType: 'email_field',
              recipient: 'Email'
            } as SendEmailConfig
          },
          {
            id: id('act', 46),
            type: 'create_task',
            name: 'Follow Up on Survey',
            order: 2,
            config: {
              subject: 'Review Survey Response: {!Contact.Name}',
              assignToType: 'owner',
              dueDateType: 'days_from_now',
              dueDays: 5,
              priority: 'Normal',
              description: 'Check if customer responded to survey. Follow up if needed.'
            } as CreateTaskConfig
          }
        ]
      }
    ],
    [id('cg', 41)]: [
      {
        id: id('sa', 12),
        name: 'Prospect Follow-up (3 days)',
        description: 'Follow-up email for prospects',
        scheduleType: 'days_after',
        scheduleValue: 3,
        baseDateField: 'CreatedDate',
        baseDateFieldLabel: 'Created Date',
        isActive: true,
        actions: [
          {
            id: id('act', 47),
            type: 'send_email',
            name: 'Send Follow-up Email',
            order: 1,
            config: {
              templateId: 'prospect_followup_template',
              templateName: 'Prospect Follow-up',
              recipientType: 'email_field',
              recipient: 'Email'
            } as SendEmailConfig
          }
        ]
      },
      {
        id: id('sa', 13),
        name: 'Prospect Offer (7 days)',
        description: 'Send special offer to engaged prospects',
        scheduleType: 'days_after',
        scheduleValue: 7,
        baseDateField: 'CreatedDate',
        baseDateFieldLabel: 'Created Date',
        isActive: true,
        actions: [
          {
            id: id('act', 48),
            type: 'send_email',
            name: 'Send Special Offer',
            order: 1,
            config: {
              templateId: 'prospect_offer_template',
              templateName: 'Special Offer',
              recipientType: 'email_field',
              recipient: 'Email'
            } as SendEmailConfig
          }
        ]
      }
    ]
  },

  currentVersion: 3,
  versions: [
    {
      id: id('pv', 40),
      versionNumber: 1,
      status: 'inactive',
      createdDate: daysAgo(120),
      createdBy: 'marketing_user',
      activatedDate: daysAgo(118),
      activatedBy: 'marketing_user',
      deactivatedDate: daysAgo(90),
      deactivatedBy: 'marketing_user',
      description: 'Initial version'
    },
    {
      id: id('pv', 41),
      versionNumber: 2,
      status: 'inactive',
      createdDate: daysAgo(90),
      createdBy: 'marketing_user',
      activatedDate: daysAgo(88),
      activatedBy: 'marketing_user',
      deactivatedDate: daysAgo(30),
      deactivatedBy: 'marketing_user',
      description: 'Added prospect sequence'
    },
    {
      id: id('pv', 42),
      versionNumber: 3,
      status: 'active',
      createdDate: daysAgo(30),
      createdBy: 'marketing_user',
      activatedDate: daysAgo(28),
      activatedBy: 'marketing_user',
      description: 'Added survey follow-up task'
    }
  ],

  createdDate: daysAgo(120),
  createdBy: 'marketing_user',
  createdByName: 'Marketing Manager',
  lastModifiedDate: daysAgo(28),
  lastModifiedBy: 'marketing_user',
  lastModifiedByName: 'Marketing Manager',
  lastRunDate: daysAgo(0),
  executionCount: 412,
  successCount: 408,
  failureCount: 4
};

/**
 * Draft Process: Lead Qualification (inactive)
 * Example of a draft process that's not yet active
 */
const leadQualificationDraft: Process = {
  id: id('proc', 6),
  name: 'Lead Qualification Workflow',
  description: 'Automated lead qualification and scoring based on engagement and profile data.',
  apiName: 'lead_qualification_workflow',
  objectType: 'Lead',
  triggerType: 'record_updated',
  isActive: false,
  status: 'draft',
  category: 'Marketing',

  entryCriteria: {
    id: id('ec', 6),
    name: 'Entry Criteria',
    conditionLogic: 'AND',
    conditions: [
      {
        id: id('cond', 50),
        field: 'Status',
        operator: 'not_equals',
        value: 'Qualified'
      }
    ],
    order: 0
  },

  reevaluateOnUpdate: true,

  criteriaGroups: [
    {
      id: id('cg', 50),
      name: 'Ready to Qualify',
      description: 'Lead meets qualification threshold',
      conditionLogic: 'AND',
      conditions: [
        {
          id: id('cond', 51),
          field: 'LeadScore',
          operator: 'greater_or_equal',
          value: 50,
          valueType: 'number'
        },
        {
          id: id('cond', 52),
          field: 'Email',
          operator: 'is_not_null',
          value: null
        },
        {
          id: id('cond', 53),
          field: 'Phone',
          operator: 'is_not_null',
          value: null
        }
      ],
      order: 1
    }
  ],

  immediateActions: {
    [id('cg', 50)]: [
      {
        id: id('act', 50),
        type: 'field_update',
        name: 'Mark as Qualified',
        order: 1,
        config: {
          targetObject: 'current',
          fieldUpdates: [
            {
              field: 'Status',
              fieldLabel: 'Status',
              updateType: 'literal',
              value: 'Qualified'
            }
          ]
        } as FieldUpdateConfig
      }
    ]
  },

  scheduledActions: {},

  currentVersion: 1,
  versions: [
    {
      id: id('pv', 50),
      versionNumber: 1,
      status: 'draft',
      createdDate: daysAgo(5),
      createdBy: 'marketing_user',
      description: 'Initial draft'
    }
  ],

  createdDate: daysAgo(5),
  createdBy: 'marketing_user',
  createdByName: 'Marketing Manager',
  lastModifiedDate: daysAgo(2),
  lastModifiedBy: 'marketing_user',
  lastModifiedByName: 'Marketing Manager',
  executionCount: 0,
  successCount: 0,
  failureCount: 0
};

// ============================================
// MOCK EXECUTIONS
// ============================================

/**
 * Sample execution records
 */
export const mockProcessExecutions: ProcessExecution[] = [
  {
    id: 'exec_mock_1',
    processId: id('proc', 1),
    processName: 'Opportunity Won - Account Update',
    versionNumber: 1,
    recordId: 'opp_001',
    recordName: 'Acme Corp - Enterprise License',
    objectType: 'Opportunity',
    triggerType: 'record_updated',
    matchedCriteriaGroupId: id('cg', 1),
    matchedCriteriaGroupName: 'High Value Deal (>$100K)',
    status: 'completed',
    startTime: daysAgo(1),
    endTime: new Date(new Date(daysAgo(1)).getTime() + 1234).toISOString(),
    duration: 1234,
    executedActions: [
      {
        actionId: id('act', 1),
        actionName: 'Update Account - High Value',
        actionType: 'field_update',
        status: 'success',
        executedAt: daysAgo(1),
        duration: 45
      },
      {
        actionId: id('act', 2),
        actionName: 'Create Executive Follow-up Task',
        actionType: 'create_task',
        status: 'success',
        executedAt: daysAgo(1),
        duration: 78,
        createdRecordId: 'task_001'
      },
      {
        actionId: id('act', 3),
        actionName: 'Send VIP Thank You Email',
        actionType: 'send_email',
        status: 'success',
        executedAt: daysAgo(1),
        duration: 234
      },
      {
        actionId: id('act', 4),
        actionName: 'Celebrate Win',
        actionType: 'post_to_feed',
        status: 'success',
        executedAt: daysAgo(1),
        duration: 56
      }
    ]
  },
  {
    id: 'exec_mock_2',
    processId: id('proc', 2),
    processName: 'High-Value Lead Assignment',
    versionNumber: 2,
    recordId: 'lead_001',
    recordName: 'John Smith - TechCorp',
    objectType: 'Lead',
    triggerType: 'record_created',
    matchedCriteriaGroupId: id('cg', 10),
    matchedCriteriaGroupName: 'Enterprise Lead',
    status: 'completed',
    startTime: daysAgo(0),
    endTime: new Date(new Date(daysAgo(0)).getTime() + 567).toISOString(),
    duration: 567,
    executedActions: [
      {
        actionId: id('act', 10),
        actionName: 'Assign to Enterprise Team',
        actionType: 'field_update',
        status: 'success',
        executedAt: daysAgo(0),
        duration: 34
      },
      {
        actionId: id('act', 11),
        actionName: 'Priority Enterprise Follow-up',
        actionType: 'create_task',
        status: 'success',
        executedAt: daysAgo(0),
        duration: 89,
        createdRecordId: 'task_002'
      }
    ]
  },
  {
    id: 'exec_mock_3',
    processId: id('proc', 3),
    processName: 'Case Escalation Process',
    versionNumber: 1,
    recordId: 'case_001',
    recordName: 'Critical Issue - Production Down',
    objectType: 'Case',
    triggerType: 'record_updated',
    matchedCriteriaGroupId: id('cg', 20),
    matchedCriteriaGroupName: 'VIP Customer',
    status: 'failed',
    startTime: daysAgo(2),
    endTime: new Date(new Date(daysAgo(2)).getTime() + 2345).toISOString(),
    duration: 2345,
    executedActions: [
      {
        actionId: id('act', 20),
        actionName: 'Set VIP Escalation Fields',
        actionType: 'field_update',
        status: 'success',
        executedAt: daysAgo(2),
        duration: 45
      },
      {
        actionId: id('act', 21),
        actionName: 'Notify Executive Team',
        actionType: 'send_email',
        status: 'failed',
        executedAt: daysAgo(2),
        duration: 1200,
        errorMessage: 'Email service temporarily unavailable'
      }
    ],
    errorMessage: 'One or more actions failed',
    errorDetails: 'Email action failed: Email service temporarily unavailable'
  }
];

// ============================================
// EXPORTS
// ============================================

/**
 * All mock processes
 */
export const mockProcesses: Process[] = [
  opportunityWonProcess,
  highValueLeadProcess,
  caseEscalationProcess,
  contractRenewalProcess,
  welcomeEmailProcess,
  leadQualificationDraft
];

/**
 * Default export
 */
export default {
  processes: mockProcesses,
  executions: mockProcessExecutions
};
